/**
 * JobPollerContext — single shared polling mechanism for all processing jobs.
 *
 * Replaces 15+ individual setIntervals across components with one poller that:
 * - Polls GET /processing/projects/{project_id}/active every 5s
 * - Pauses when the browser tab is hidden
 * - Stops polling when there are no active jobs (resumes on new job)
 * - Exposes jobs array + helpers to any subscriber
 *
 * Usage:
 *   const { jobs, activeCount, getJob, registerJob } = useJobs();
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';

const JobPollerContext = createContext({
  jobs: [],
  activeCount: 0,
  getJob: () => null,
  registerJob: () => {},
});

export function JobPollerProvider({ projectId, children }) {
  const [jobs, setJobs] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const registeredJobIds = useRef(new Set());
  const intervalRef = useRef(null);
  const hasActiveRef = useRef(false);

  const fetchJobs = useCallback(async () => {
    if (!projectId || document.hidden) return;
    try {
      const res = await api.getActiveJobs(projectId);
      const allJobs = Array.isArray(res.data?.jobs) ? res.data.jobs : [];
      setJobs(allJobs);
      const active = res.data?.active_count || 0;
      setActiveCount(active);
      hasActiveRef.current = active > 0;
    } catch {
      // Silent fail — non-critical
    }
  }, [projectId]);

  // Register a new job ID to trigger polling if idle
  const registerJob = useCallback((jobId) => {
    if (jobId) {
      registeredJobIds.current.add(jobId);
      hasActiveRef.current = true;
      // Immediate fetch to pick up the new job
      fetchJobs();
    }
  }, [fetchJobs]);

  // Get a specific job by ID
  const getJob = useCallback((jobId) => {
    return jobs.find(j => j.job_id === jobId) || null;
  }, [jobs]);

  // Single polling loop — always runs when projectId is set, pauses when tab hidden
  useEffect(() => {
    if (!projectId) return;

    fetchJobs(); // initial fetch

    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchJobs();
    }, 5000);

    // Immediate fetch when tab becomes visible
    const handleVisibility = () => {
      if (!document.hidden) fetchJobs();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [projectId, fetchJobs]);

  // Clean up registered job IDs when they complete
  useEffect(() => {
    for (const jobId of registeredJobIds.current) {
      const job = jobs.find(j => j.job_id === jobId);
      if (job && (job.status === 'completed' || job.status === 'failed')) {
        registeredJobIds.current.delete(jobId);
      }
    }
  }, [jobs]);

  return (
    <JobPollerContext.Provider value={{ jobs, activeCount, getJob, registerJob }}>
      {children}
    </JobPollerContext.Provider>
  );
}

export const useJobs = () => useContext(JobPollerContext);
export default JobPollerContext;
